import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Spinner } from '../../../shared/components';
import { formatDateTime } from '../../../shared/utils/localization';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionScopes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import { listReminderDefinitions, listScheduledReminders, processScheduledReminder } from '../services/reminderEngineService';
import type { ReminderEngineScheduledItem, ScheduledReminderStatus } from '../types/ReminderEngine';
import { getReminderStatusClass } from '../utils/reminderEngineStatus';
import { ScheduledEntityCell } from './ScheduledEntityCell';
import sharedStyles from './reminderEngineShared.module.css';

const PAGE_SIZE = 25;

export const ReminderScheduledPage: React.FC = () => {
  const { t } = useTranslation(['reminderEngine', 'common']);
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const canManage = hasPermission(PermissionScopes.remindersManage);
  const [searchParams, setSearchParams] = useSearchParams();

  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [scheduled, setScheduled] = React.useState<ReminderEngineScheduledItem[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [definitionKeys, setDefinitionKeys] = React.useState<Array<{ key: string; name: string }>>([]);
  const [expandedId, setExpandedId] = React.useState<string | null>(null);

  const definitionKeyFilter = searchParams.get('definitionKey') ?? '';
  const statusFilter = (searchParams.get('status') ?? '') as ScheduledReminderStatus | '';
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);

  const updateFilters = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(patch).forEach(([key, value]) => {
      if (!value) {
        next.delete(key);
      } else {
        next.set(key, value);
      }
    });
    if (!patch.page) {
      next.set('page', '1');
    }
    setSearchParams(next, { replace: true });
  };

  const loadData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [definitionItems, scheduledPage] = await Promise.all([
        listReminderDefinitions(),
        listScheduledReminders({
          definitionKey: definitionKeyFilter || undefined,
          status: statusFilter || undefined,
          page,
          pageSize: PAGE_SIZE,
        }),
      ]);
      setDefinitionKeys(
        Array.from(
          new Map(definitionItems.map((item) => [item.key, { key: item.key, name: item.name }])).values(),
        ).sort((a, b) => a.name.localeCompare(b.name)),
      );
      setScheduled(scheduledPage.items);
      setTotalCount(scheduledPage.totalCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [definitionKeyFilter, page, statusFilter, t]);

  React.useEffect(() => {
    void loadData();
  }, [loadData]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleProcess = async (scheduledReminderId: string) => {
    try {
      const result = await processScheduledReminder(scheduledReminderId);
      const deliveryErrors = result.deliveries
        .filter((delivery) => delivery.error)
        .map((delivery) => `${delivery.channelKey}: ${delivery.error}`)
        .join('; ');

      if (deliveryErrors) {
        showToast(`${t('reminderProcessedWithErrors')}: ${deliveryErrors}`, 'warning');
      } else {
        showToast(t('reminderProcessed'), 'success');
      }
      await loadData();
    } catch (err) {
      showToast(err instanceof Error ? err.message : t('actionFailed'), 'error');
    }
  };

  const hasDeliveryDetails = (item: ReminderEngineScheduledItem) =>
    (item.deliveries?.length ?? 0) > 0
    && ['failed', 'partiallycompleted', 'skipped'].includes(item.status.toLowerCase());

  const toggleExpanded = (itemId: string) => {
    setExpandedId((current) => (current === itemId ? null : itemId));
  };

  const subjectTypeLabel = (type: string) => {
    const normalized = type.toLowerCase();
    if (normalized === 'task') return t('subjectTypeTask');
    if (normalized === 'appointment') return t('subjectTypeAppointment');
    return type;
  };

  const recipientTypeLabel = (type: string) => {
    const normalized = type.toLowerCase();
    if (normalized === 'employee' || normalized === 'user') return t('recipientTypeEmployee');
    if (normalized === 'contact') return t('recipientTypeContact');
    return type;
  };

  return (
    <section className={sharedStyles.sectionCard}>
      <div className={sharedStyles.headerRow}>
        <div>
          <h3 className={sharedStyles.sectionTitle}>{t('scheduledTitle')}</h3>
          <p className={sharedStyles.emptyState}>{t('scheduledSubtitle')}</p>
        </div>
        <button type="button" className="btn btn-secondary btn-sm" onClick={() => void loadData()} disabled={loading}>
          {t('refresh', { ns: 'common' })}
        </button>
      </div>

      <div className={sharedStyles.filters}>
        <label>
          {t('definitionKey')}
          <select
            value={definitionKeyFilter}
            onChange={(event) => updateFilters({ definitionKey: event.target.value || null })}
          >
            <option value="">{t('allDefinitions')}</option>
            {definitionKeys.map((definition) => (
              <option key={definition.key} value={definition.key}>{definition.name}</option>
            ))}
          </select>
        </label>
        <label>
          {t('status')}
          <select
            value={statusFilter}
            onChange={(event) => updateFilters({ status: event.target.value || null })}
          >
            <option value="">{t('all', { ns: 'common' })}</option>
            <option value="pending">pending</option>
            <option value="processing">processing</option>
            <option value="completed">completed</option>
            <option value="partiallyCompleted">partiallyCompleted</option>
            <option value="failed">failed</option>
            <option value="cancelled">cancelled</option>
            <option value="skipped">skipped</option>
          </select>
        </label>
      </div>

      {loading && <Spinner />}
      {error && <p className={sharedStyles.error}>{error}</p>}

      {!loading && !error && scheduled.length === 0 && (
        <p className={sharedStyles.emptyState}>{t('noScheduled')}</p>
      )}

      {!loading && !error && scheduled.length > 0 && (
        <>
          <p className={sharedStyles.emptyState}>{t('scheduledCount', { count: totalCount })}</p>
          <table className={sharedStyles.table}>
            <thead>
              <tr>
                <th>{t('definition')}</th>
                <th>{t('subject')}</th>
                <th>{t('recipient')}</th>
                <th>{t('scheduledAt')}</th>
                <th>{t('status')}</th>
                {canManage && <th>{t('actions', { ns: 'common' })}</th>}
              </tr>
            </thead>
            <tbody>
              {scheduled.map((item) => (
                <React.Fragment key={item.id}>
                  <tr>
                    <td>
                      <div className={sharedStyles.definitionNameCell}>
                        <strong>{item.definitionName || item.definitionKey || '—'}</strong>
                        {item.definitionKey ? (
                          <span className={sharedStyles.definitionKey}>{item.definitionKey}</span>
                        ) : null}
                      </div>
                    </td>
                    <td>
                      <ScheduledEntityCell
                        displayName={item.subjectDisplayName}
                        entityType={item.subjectType}
                        entityId={item.subjectId}
                        typeLabel={subjectTypeLabel(item.subjectType)}
                      />
                    </td>
                    <td>
                      <ScheduledEntityCell
                        displayName={item.recipientDisplayName}
                        entityType={item.recipientType}
                        entityId={item.recipientId}
                        typeLabel={recipientTypeLabel(item.recipientType)}
                      />
                    </td>
                    <td>{formatDateTime(item.scheduledAt)}</td>
                    <td>
                      <div className={sharedStyles.statusCell}>
                        <span className={getReminderStatusClass(item.status)}>{item.status}</span>
                        {item.failureSummary ? (
                          <p className={sharedStyles.failureSummary}>{item.failureSummary}</p>
                        ) : null}
                        {hasDeliveryDetails(item) ? (
                          <button
                            type="button"
                            className={sharedStyles.deliveryToggle}
                            onClick={() => toggleExpanded(item.id)}
                          >
                            {expandedId === item.id ? t('hideDeliveryAttempts') : t('showDeliveryAttempts')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                    {canManage && (
                      <td>
                        {(item.status === 'pending' || item.status === 'failed') && (
                          <button type="button" className="btn btn-secondary btn-sm" onClick={() => void handleProcess(item.id)}>
                            {t('processNow')}
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  {expandedId === item.id && item.deliveries && item.deliveries.length > 0 ? (
                    <tr className={sharedStyles.expandedRow}>
                      <td colSpan={canManage ? 6 : 5}>
                        <div className={sharedStyles.deliveryPanel}>
                          <p className={sharedStyles.deliveryPanelTitle}>{t('deliveryAttempts')}</p>
                          <table className={sharedStyles.deliveryTable}>
                            <thead>
                              <tr>
                                <th>{t('channel')}</th>
                                <th>{t('status')}</th>
                                <th>{t('attemptedAt')}</th>
                                <th>{t('failureReason')}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.deliveries.map((delivery) => (
                                <tr key={delivery.id}>
                                  <td>{delivery.channelKey}</td>
                                  <td><span className={getReminderStatusClass(delivery.status)}>{delivery.status}</span></td>
                                  <td>{delivery.attemptedAt ? formatDateTime(delivery.attemptedAt) : '—'}</td>
                                  <td className={delivery.error ? sharedStyles.deliveryError : undefined}>
                                    {delivery.error || '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  ) : null}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div className={sharedStyles.pagination}>
            <span className={sharedStyles.paginationMeta}>
              {t('pageSummary', { page, totalPages, totalCount })}
            </span>
            <div className={sharedStyles.paginationActions}>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page <= 1 || loading}
                onClick={() => updateFilters({ page: String(page - 1) })}
              >
                {t('previousPage')}
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                disabled={page >= totalPages || loading}
                onClick={() => updateFilters({ page: String(page + 1) })}
              >
                {t('nextPage')}
              </button>
            </div>
          </div>
        </>
      )}
    </section>
  );
};
